// Studio — newscast episode list + composer.
//
// An editor picks 2-5 published news articles and creates an episode; the
// rundown itself is edited on /studio/:id. Named "Studio" rather than
// "Newscast" or "Broadcast" because both of those already mean something else
// in this codebase (a plagiarism-corpus client and push-alert blasts).
//
// Backend endpoints (editor-gated, and 404 unless STUDIO_ENABLED=true):
//   GET  /studio/episodes?limit=&cursor=
//   POST /studio/episodes   { title, article_ids[2..5], anchor_mode }
//   GET  /editor/dashboard/published   — the article source for the picker
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Input, Label, Select } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { api } from "../../lib/api";

type AnchorMode = "avatar" | "obs";

interface EpisodeRow {
  id: string;
  title: string;
  status: string;
  anchor_mode: AnchorMode;
  scheduled_air_at: string | null;
  duration_seconds: number | null;
  aired_at: string | null;
  last_error: string | null;
  created_at: string;
}

interface PublishedRow {
  id: string;
  title: string;
  summary: string | null;
  image_url: string | null;
  published_at: string;
}

const MIN_STORIES = 2;
const MAX_STORIES = 5;

export default function StudioPage() {
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [anchorMode, setAnchorMode] = useState<AnchorMode>("avatar");
  const [selected, setSelected] = useState<string[]>([]);

  const [published, setPublished] = useState<PublishedRow[] | null>(null);
  const [episodes, setEpisodes] = useState<EpisodeRow[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadEpisodes = useCallback(async () => {
    try {
      const r = await api<{ episodes: EpisodeRow[] }>(
        "/studio/episodes?limit=25",
      );
      setEpisodes(r.episodes);
    } catch (e: any) {
      // A disabled Studio (404) is a configuration state, not a page error —
      // surface it inline instead of blanking the list forever.
      setEpisodes([]);
      if (e?.status === 404) {
        setError(
          "Studio is not enabled on this API (set STUDIO_ENABLED=true).",
        );
      } else {
        console.warn("Failed to load episodes", e);
      }
    }
  }, []);

  const loadPublished = useCallback(async () => {
    try {
      const r = await api<{ published: PublishedRow[] }>(
        "/editor/dashboard/published?limit=50",
      );
      setPublished(r.published);
    } catch (e) {
      console.warn("Failed to load published articles", e);
      setPublished([]);
    }
  }, []);

  useEffect(() => {
    void loadEpisodes();
    void loadPublished();
  }, [loadEpisodes, loadPublished]);

  // Order matters — selection order becomes the running order, so append
  // rather than reinserting at the article's position in the source list.
  function toggleArticle(id: string) {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((a) => a !== id)
        : prev.length >= MAX_STORIES
          ? prev
          : [...prev, id],
    );
  }

  function validate(): string | null {
    if (!title.trim()) return "Give the episode a title.";
    if (selected.length < MIN_STORIES)
      return `Pick at least ${MIN_STORIES} stories.`;
    if (selected.length > MAX_STORIES)
      return `Pick at most ${MAX_STORIES} stories.`;
    return null;
  }

  async function create() {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setCreating(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api<{ episode: EpisodeRow }>("/studio/episodes", {
        method: "POST",
        body: {
          title: title.trim(),
          article_ids: selected,
          anchor_mode: anchorMode,
        },
      });
      navigate(`/studio/${r.episode.id}`);
    } catch (e: any) {
      setError(e.message ?? "Could not create the episode");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Studio</h1>
        <CardSubtle>
          Assemble published news articles into a newscast for Twitch and
          YouTube.
        </CardSubtle>
      </div>

      <div style={styles.grid}>
        <Card>
          <CardTitle>New episode</CardTitle>

          <div style={{ marginTop: 16 }}>
            <Label>Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Global Village News — Tuesday"
              maxLength={200}
            />
          </div>

          <div style={{ marginTop: 16 }}>
            <Label>Anchor</Label>
            <Select
              value={anchorMode}
              onChange={(e) => setAnchorMode(e.target.value as AnchorMode)}
            >
              <option value="avatar">AI avatar (rendered)</option>
              <option value="obs">Human host (live from OBS)</option>
            </Select>
          </div>

          <div style={{ marginTop: 16 }}>
            <Label>
              Stories — {selected.length}/{MAX_STORIES} selected, in running
              order
            </Label>
            {published === null ? (
              <Spinner />
            ) : published.length === 0 ? (
              <CardSubtle>
                No published articles yet. Publish some from the Dashboard
                first.
              </CardSubtle>
            ) : (
              <div style={styles.articleGrid}>
                {published.map((a) => {
                  const idx = selected.indexOf(a.id);
                  const isSelected = idx >= 0;
                  const atCap = !isSelected && selected.length >= MAX_STORIES;
                  return (
                    <label
                      key={a.id}
                      style={{
                        ...styles.articleRow,
                        opacity: atCap ? 0.4 : 1,
                        cursor: atCap ? "not-allowed" : "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={atCap}
                        onChange={() => toggleArticle(a.id)}
                      />
                      {isSelected && (
                        <span style={styles.orderBadge}>{idx + 1}</span>
                      )}
                      <span style={{ flex: 1 }}>{a.title}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ marginTop: 20 }}>
            <Button onClick={create} disabled={creating}>
              {creating ? <Spinner size={12} /> : "Create episode"}
            </Button>
            {error && <div style={styles.error}>{error}</div>}
            {notice && <div style={styles.notice}>{notice}</div>}
          </div>
        </Card>

        <Card>
          <CardTitle>How it works</CardTitle>
          <CardSubtle>
            Creating an episode only records the running order. Next you
            generate the rundown — the anchor script broken into beats — then
            attach media and render.
          </CardSubtle>
          <ul style={styles.helpList}>
            <li>
              <strong>Anchor on cam</strong> — the anchor talks, no media.
            </li>
            <li>
              <strong>VO</strong> — the anchor talks over muted b-roll.
            </li>
            <li>
              <strong>SOT</strong> — the anchor goes silent and a clip plays
              with its own audio.
            </li>
          </ul>
        </Card>
      </div>

      <div style={{ marginTop: 24 }}>
        <CardTitle>Episodes</CardTitle>
        {episodes === null ? (
          <Spinner />
        ) : episodes.length === 0 ? (
          <CardSubtle>No episodes yet.</CardSubtle>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Anchor</th>
                  <th style={styles.th}>Length</th>
                  <th style={styles.th}>Created</th>
                </tr>
              </thead>
              <tbody>
                {episodes.map((ep) => (
                  <tr
                    key={ep.id}
                    style={{ ...styles.tr, cursor: "pointer" }}
                    onClick={() => navigate(`/studio/${ep.id}`)}
                  >
                    <td style={styles.td}>
                      {ep.title}
                      {ep.last_error && (
                        <div style={styles.errorSubline}>{ep.last_error}</div>
                      )}
                    </td>
                    <td style={styles.td}>{ep.status}</td>
                    <td style={styles.td}>
                      {ep.anchor_mode === "avatar"
                        ? "AI avatar"
                        : "Human / OBS"}
                    </td>
                    <td style={styles.td}>
                      {formatDuration(ep.duration_seconds)}
                    </td>
                    <td style={styles.td}>{formatRelative(ep.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatRelative(ts: string | null | undefined): string {
  if (!ts) return "—";
  const d = new Date(ts);
  const diff = Date.now() - d.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
}

const styles: Record<string, React.CSSProperties> = {
  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
    gap: 16,
    alignItems: "start",
  },
  articleGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    maxHeight: 320,
    overflow: "auto",
    padding: 8,
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    background: "var(--bg)",
  },
  articleRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "var(--text-secondary)",
    padding: "4px 4px",
  },
  orderBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    background: "var(--accent)",
    color: "#000",
    fontSize: 11,
    fontWeight: 700,
  },
  helpList: {
    marginTop: 12,
    paddingLeft: 18,
    fontSize: 13,
    color: "var(--text-secondary)",
    lineHeight: 1.7,
  },
  error: {
    marginTop: 8,
    fontSize: 13,
    color: "var(--danger)",
  },
  errorSubline: {
    marginTop: 4,
    fontSize: 12,
    color: "var(--danger)",
  },
  notice: {
    marginTop: 8,
    fontSize: 13,
    color: "var(--success)",
  },
  tableWrap: {
    marginTop: 12,
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-md)",
    overflow: "hidden",
    background: "var(--surface)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "12px 16px",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "var(--text-muted)",
    borderBottom: "1px solid var(--border)",
    background: "rgba(255,255,255,0.02)",
  },
  tr: {
    borderBottom: "1px solid var(--border)",
  },
  td: {
    padding: "12px 16px",
    fontSize: 14,
    color: "var(--text-primary)",
    verticalAlign: "top",
  },
};
